// Presentation vocabulary for exception governance (APP-004 R3 UI-P5).
//
// Every state here changes what a reader believes about risk: a revoked or
// expired risk acceptance must never read as cover, an ineffective
// compensating control must not read as a control, and an exception the
// backend closed by revocation must not read as merely closed. The backend
// names the state; this module only decides how it looks.
import { Badge } from "@odingard/design-system";
import type { BadgeTone } from "@odingard/design-system";
import type {
  ControlEffectiveness,
  ExceptionRiskLevel,
  ExceptionRouteMode,
  ExceptionVoteDecision,
  RiskAcceptanceState,
} from "@odingard/sdk-client";

const STATUS: Record<string, { label: string; tone: BadgeTone }> = {
  requested: { label: "Requested", tone: "info" },
  changes_requested: { label: "Changes requested", tone: "warning" },
  approved: { label: "Approved", tone: "success" },
  rejected: { label: "Rejected", tone: "danger" },
  expired: { label: "Expired", tone: "warning" },
  closed: { label: "Closed", tone: "neutral" },
};

export function ExceptionStatusBadge({ status }: { status: string }) {
  const it = STATUS[status] ?? { label: status, tone: "neutral" as BadgeTone };
  return <Badge tone={it.tone}>{it.label}</Badge>;
}

const RISK: Record<ExceptionRiskLevel, BadgeTone> = {
  low: "neutral",
  medium: "info",
  high: "warning",
  critical: "danger",
};

export function RiskLevelBadge({ level }: { level: ExceptionRiskLevel | null }) {
  if (level === null) return <span className="muted-note">Not classified</span>;
  return <Badge tone={RISK[level]}>{level}</Badge>;
}

const EFFECTIVENESS: Record<ControlEffectiveness, { label: string; tone: BadgeTone }> = {
  unassessed: { label: "Unassessed", tone: "neutral" },
  effective: { label: "Effective", tone: "success" },
  partially_effective: { label: "Partially effective", tone: "warning" },
  ineffective: { label: "Ineffective", tone: "danger" },
};

export function EffectivenessBadge({ effectiveness }: { effectiveness: ControlEffectiveness }) {
  const it = EFFECTIVENESS[effectiveness];
  return <Badge tone={it.tone}>{it.label}</Badge>;
}

export function effectivenessLabel(effectiveness: ControlEffectiveness): string {
  return EFFECTIVENESS[effectiveness].label;
}

const ACCEPTANCE: Record<RiskAcceptanceState, { label: string; tone: BadgeTone }> = {
  none: { label: "No risk acceptance", tone: "neutral" },
  active: { label: "Risk accepted", tone: "success" },
  expired: { label: "Acceptance expired", tone: "warning" },
  revoked: { label: "Acceptance revoked", tone: "danger" },
};

export function AcceptanceStateBadge({ state }: { state: RiskAcceptanceState }) {
  const it = ACCEPTANCE[state];
  return <Badge tone={it.tone}>{it.label}</Badge>;
}

const VOTE: Record<ExceptionVoteDecision, { label: string; tone: BadgeTone }> = {
  approved: { label: "Approved", tone: "success" },
  rejected: { label: "Rejected", tone: "danger" },
  changes_requested: { label: "Changes requested", tone: "warning" },
};

export function VoteDecisionBadge({ decision }: { decision: ExceptionVoteDecision }) {
  const it = VOTE[decision] ?? { label: decision, tone: "neutral" as BadgeTone };
  return <Badge tone={it.tone}>{it.label}</Badge>;
}

export function routeModeLabel(mode: ExceptionRouteMode): string {
  return mode === "sequential" ? "Sequential" : "Parallel";
}

export const EXCEPTION_STATUSES = [
  "requested",
  "changes_requested",
  "approved",
  "rejected",
  "expired",
  "closed",
];

/**
 * Overdue is derived from the backend's approved end date only. An exception
 * with no end date is never overdue, whatever else is true of it.
 */
export function isOverdue(status: string, approvedEnd: string | null): boolean {
  if (status !== "approved" || approvedEnd === null) return false;
  return approvedEnd < new Date().toISOString().slice(0, 10);
}

/** Review due is likewise the backend's review date against today. */
export function isReviewDue(status: string, reviewAt: string | null): boolean {
  if (status !== "approved" || reviewAt === null) return false;
  return reviewAt <= new Date().toISOString().slice(0, 10);
}
